/**
 * AppShell — The standard wrapper for every EduTool.
 *
 * Responsibilities:
 *   - Initialize bridge, i18n, event bus
 *   - Mount the sidebar with its 6 standard sections
 *   - Mount the persistent buttons (fullscreen, snapshot, sidebar toggle)
 *   - Host the tool's stage content
 *   - Handle splash screen lifecycle
 *   - React to host commands (theme change, locale change, etc.)
 *
 * Usage:
 *   const shell = new AppShell({
 *     manifest,
 *     sections: { ... },
 *     stageRenderer: MyStageRenderer,
 *     defaultSidebarOpen: false,
 *   });
 *   await shell.boot();
 */

import type { BaseSection, SectionId } from './BaseSection';
import type { ComponentContext } from './BaseComponent';
import { BaseComponent } from './BaseComponent';
import { EventBus } from './EventBus';
import { SplashScreen } from './SplashScreen';
import { applySafeArea } from './safe-area';
import { EduToolBridge } from '@/bridge/edutool-bridge';
import type {
  BridgeMessage,
  InitPayload,
  Locale,
  Theme,
  ToolManifest,
  UserRole,
} from '@/bridge/bridge-types';
import { I18nService } from '@/i18n/I18nService';
import { setI18n } from '@/i18n/i18n-registry';
import { PersistentButtons } from '@/components/buttons/PersistentButtons';

export type SectionConstructor = new (
  element: HTMLElement,
  context: ComponentContext,
) => BaseSection;

export type StageConstructor = new (
  element: HTMLElement,
  context: ComponentContext,
) => BaseComponent;

export interface AppShellConfig {
  manifest: ToolManifest;
  sections: Partial<Record<SectionId, SectionConstructor>>;
  stageRenderer: StageConstructor;
  defaultSidebarOpen?: boolean;
}

export class AppShell {
  private readonly config: AppShellConfig;
  private readonly splash: SplashScreen;
  private readonly bus: EventBus;

  private bridge!: EduToolBridge;
  private i18n!: I18nService;
  private context!: ComponentContext;

  private sidebarEl!: HTMLElement;
  private stageEl!: HTMLElement;
  private stageComponent!: BaseComponent;
  private persistentButtons!: PersistentButtons;
  private sections: Partial<Record<SectionId, BaseSection>> = {};

  private sidebarOpen: boolean;

  constructor(config: AppShellConfig) {
    this.config = config;
    this.splash = new SplashScreen();
    this.bus = new EventBus();
    // Default sidebar: aperta solo su schermi ampi (PC). Su tablet/telefono
    // parte CHIUSA (drawer overlay). Su LIM parte SEMPRE chiusa.
    const lim = typeof document !== 'undefined' && document.documentElement.dataset.surface === 'lim';
    const wide = typeof window === 'undefined' || window.innerWidth >= 1024;
    this.sidebarOpen = (config.defaultSidebarOpen ?? false) && wide && !lim;
  }

  /**
   * Boot the entire tool: bridge init → i18n load → mount UI → remove splash.
   */
  async boot(): Promise<void> {
    try {
      this.splash.setSubtitle('Inizializzazione...');
      this.splash.setProgress(5);

      // 1. Bridge
      this.bridge = new EduToolBridge(this.config.manifest);
      const initPayload: InitPayload = await this.bridge.init();
      this.splash.setProgress(20);

      // 2. Safe area (from host or fallback to CSS env)
      if (initPayload.safeArea) {
        applySafeArea(initPayload.safeArea);
      }

      // 3. Theme
      this.applyTheme(initPayload.theme);
      this.splash.setProgress(25);

      // 4. i18n
      this.splash.setSubtitle('Caricamento lingua...');
      // Lingue dal manifest (per-tool): il servizio è INVARIANTE, non deve
      // contenere l'elenco di un tool specifico. La prima è la primaria.
      this.i18n = new I18nService(this.config.manifest.locale);
      await this.i18n.init(initPayload.locale);
      // Chi non è un componente (lesson-builder-service) prende da qui.
      setI18n(this.i18n);
      this.splash.setProgress(40);

      // 5. Build context
      this.context = {
        role: initPayload.role,
        locale: initPayload.locale,
        theme: initPayload.theme,
        bridge: this.bridge,
        i18n: this.i18n,
        bus: this.bus,
      };

      // 6. Mount DOM structure
      this.splash.setSubtitle('Preparazione interfaccia...');
      this.mountStructure();
      this.splash.setProgress(55);

      // 7. Mount sidebar sections
      await this.mountSections();
      this.splash.setProgress(75);

      // 8. Mount persistent buttons
      await this.mountPersistentButtons();
      this.splash.setProgress(85);

      // 9. Mount stage (tool-specific content)
      this.splash.setSubtitle('Caricamento contenuto...');
      await this.mountStage();
      this.splash.setProgress(95);

      // 10. Wire bridge incoming messages
      this.wireBridgeCommands();

      // 11. Apply initial state
      this.setSidebarOpen(this.sidebarOpen);

      // Su TELEFONO (<768px) e su LIM la sidebar è un drawer overlay: dopo aver
      // scelto una vista si chiude da sola. Da tablet/PC (≥768px) può restare aperta.
      this.bus.on('stage:route-changed', () => {
        if (typeof window === 'undefined' || !this.sidebarOpen) return;
        const lim = document.documentElement.dataset.surface === 'lim';
        if (window.innerWidth < 768 || lim) this.setSidebarOpen(false);
      });
      this.bus.on('sidebar:close', () => this.setSidebarOpen(false));

      this.splash.setProgress(100);

      // 12. Remove splash
      await this.splash.hide();
    } catch (err) {
      console.error('[AppShell] boot failed:', err);
      this.showBootError(err);
      throw err;
    }
  }

  /**
   * Toggle the sidebar open/closed.
   */
  toggleSidebar(): void {
    this.setSidebarOpen(!this.sidebarOpen);
  }

  setSidebarOpen(open: boolean): void {
    this.sidebarOpen = open;
    this.sidebarEl.dataset.open = String(open);
    this.persistentButtons.setSidebarState(open);
  }

  /**
   * Change theme at runtime.
   */
  applyTheme(theme: Theme): void {
    document.documentElement.dataset.theme = theme;
    if (this.context) {
      this.context.theme = theme;
      Object.values(this.sections).forEach((s) => s?.applyTheme(theme));
      this.stageComponent?.applyTheme(theme);
    }
  }

  /**
   * Change locale at runtime.
   */
  async applyLocale(locale: Locale): Promise<void> {
    this.context.locale = locale;
    await this.i18n.setLocale(locale);
    // BaseComponent auto-subscribes to locale changes, so children will re-render.
  }

  /**
   * Apply a new user role to all components.
   */
  applyRole(role: UserRole): void {
    this.context.role = role;
    Object.values(this.sections).forEach((s) => s?.applyRole(role));
    this.stageComponent?.applyRole(role);
  }

  /* ============================================================
   * Private — DOM construction
   * ============================================================ */

  private mountStructure(): void {
    const appRoot = document.getElementById('app');
    if (!appRoot) throw new Error('Missing #app root element');

    // Stage
    this.stageEl = document.createElement('div');
    this.stageEl.className = 'edutool-stage';
    appRoot.appendChild(this.stageEl);

    // Sidebar
    this.sidebarEl = document.createElement('aside');
    this.sidebarEl.className = 'edutool-sidebar';
    this.sidebarEl.dataset.open = String(this.sidebarOpen);
    this.sidebarEl.setAttribute('aria-label', 'Tool sidebar');
    document.body.appendChild(this.sidebarEl);

    // Build 6 section slots in order
    const order: SectionId[] = ['brand', 'nav', 'search', 'list', 'functions', 'tray'];
    for (const id of order) {
      const slot = document.createElement('div');
      slot.className = 'edutool-sidebar-section';
      slot.dataset.section = id;
      this.sidebarEl.appendChild(slot);
    }
  }

  private async mountSections(): Promise<void> {
    const order: SectionId[] = ['brand', 'nav', 'search', 'list', 'functions', 'tray'];
    for (const id of order) {
      const Ctor = this.config.sections[id];
      if (!Ctor) continue;

      const slot = this.sidebarEl.querySelector<HTMLElement>(
        `[data-section="${id}"]`,
      );
      if (!slot) continue;

      const section = new Ctor(slot, this.context);
      await section.init();
      section.show();
      this.sections[id] = section;
    }
  }

  private async mountPersistentButtons(): Promise<void> {
    const container = document.getElementById('persistent-buttons');
    if (!container) throw new Error('Missing #persistent-buttons container');

    this.persistentButtons = new PersistentButtons(container, this.context);
    this.persistentButtons.onToggleSidebar = () => this.toggleSidebar();
    this.persistentButtons.onFullscreen = () => this.toggleFullscreen();
    this.persistentButtons.onSnapshot = () => this.captureSnapshot();
    await this.persistentButtons.init();
    this.persistentButtons.show();
  }

  private async mountStage(): Promise<void> {
    this.stageComponent = new this.config.stageRenderer(this.stageEl, this.context);
    await this.stageComponent.init();
    this.stageComponent.show();
  }

  private wireBridgeCommands(): void {
    this.bridge.on('EDUTOOL_CMD', (msg: BridgeMessage) => {
      const payload = msg.payload as { action: string; [key: string]: unknown };
      this.handleCommand(payload);
    });

    this.bridge.on('EDUTOOL_LOCALE_CHANGE', (msg: BridgeMessage) => {
      const payload = msg.payload as { locale: Locale };
      this.applyLocale(payload.locale).catch(console.error);
    });

    this.bus.on<{ locale: Locale }>('locale:change', ({ locale }) => {
      this.applyLocale(locale).catch(console.error);
    });

    this.bridge.on('EDUTOOL_THEME_CHANGE', (msg: BridgeMessage) => {
      const payload = msg.payload as { theme: Theme };
      this.applyTheme(payload.theme);
    });
  }

  private handleCommand(payload: { action: string; [key: string]: unknown }): void {
    switch (payload.action) {
      case 'toggle_sidebar':
        this.toggleSidebar();
        break;
      case 'fullscreen':
        this.toggleFullscreen();
        break;
      case 'snapshot':
        this.captureSnapshot();
        break;
      case 'navigate':
        this.bus.emit('navigate', payload);
        break;
      default:
        this.bus.emit(`cmd:${payload.action}`, payload);
    }
  }

  private async toggleFullscreen(): Promise<void> {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('[AppShell] fullscreen toggle failed:', err);
    }
  }

  private async captureSnapshot(): Promise<void> {
    // Tool-specific snapshot: emit event, let the stage component produce the image.
    // The stage component listens to 'capture:snapshot' and responds with the dataUrl.
    this.bus.emit<{ callback: (dataUrl: string) => void }>('capture:snapshot', {
      callback: (dataUrl: string) => {
        this.bridge.send('EDUTOOL_SNAPSHOT', { dataUrl });
      },
    });
  }

  private showBootError(err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    this.splash.setTitle('Errore');
    this.splash.setSubtitle(message);
  }
}
